import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import Box from "../../../setupPage/SharedComponents/Box";
import SetupTittle from "../../../setupPage/SharedComponents/SetupTittle";
import {
  crmGetOrders,
  crmGetStaff,
  crmImportOrders,
  crmMarkDelivered,
  crmSaveOrder,
  crmSaveTask,
  mapOrderCsv,
} from "../../../../api/crmApi";
import {
  AttendBar,
  AttachmentList,
  CARRIER_STATUSES,
  CrmDrawer,
  Field,
  GhostBtn,
  ImportCsvButton,
  PrimaryBtn,
  ROUTES,
  SelectInput,
  TextInput,
} from "./crmShared";

const empty = {
  invoiceNo: "",
  route: "",
  senderName: "",
  senderPhone: "",
  receiverName: "",
  itemDescription: "",
  priceCharged: "",
  paymentStatus: "Paid",
  deliveryLastStatus: "",
};

const CrmOrders = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [route, setRoute] = useState("");
  const [editing, setEditing] = useState(null);
  const [staff, setStaff] = useState([]);

  const load = async () => {
    const data = await crmGetOrders({ search, route, page: 1, pageSize: 200 });
    setRows(data.items);
  };

  useEffect(() => {
    load().catch(() => setRows([]));
    crmGetStaff().then(setStaff).catch(() => setStaff([]));
  }, [search, route]);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...editing,
      bookingDate: editing.bookingDate || editing.BookingDate || new Date().toISOString(),
      priceCharged: editing.priceCharged === "" ? null : Number(editing.priceCharged),
      boxQty: editing.boxQty === "" ? null : Number(editing.boxQty),
      weightQty: editing.weightQty === "" ? null : Number(editing.weightQty),
      carrierCost: editing.carrierCost === "" ? null : Number(editing.carrierCost),
      deliveryStatusUpdatedAt: editing.deliveryLastStatus
        ? new Date().toISOString()
        : editing.deliveryStatusUpdatedAt,
    };
    await crmSaveOrder(payload);
    setEditing(null);
    await load();
  };

  return (
    <Box top>
      <SetupTittle text="CRM Orders" />
      <p className="text-sm text-stone-500 mb-3 max-w-3xl">
        Bookings created from confirmed leads. Add the carrier tracking URL, current status, and
        delivery date. You will be notified on the delivery day.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          placeholder="Search invoice, sender, tracking"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-stone-300 rounded-xl h-10 px-3 bg-white"
        />
        <SelectInput value={route} onChange={setRoute} options={ROUTES} />
        <PrimaryBtn onClick={() => setEditing({ ...empty })}>New order</PrimaryBtn>
        <ImportCsvButton
          label="Import CSV"
          onParsed={async (text) => {
            const mapped = mapOrderCsv(text);
            const result = await crmImportOrders(mapped);
            Swal.fire({
              icon: "success",
              title: `Imported ${result.imported ?? result.Imported ?? 0}`,
            });
            await load();
          }}
        />
      </div>
      <div className="overflow-x-auto bg-white rounded-2xl border border-stone-200">
        <table className="min-w-full text-sm">
          <thead className="bg-red-100">
            <tr>
              {["Invoice", "Parcel", "Date", "Route", "Sender", "Price", "Carrier", "Delivery date", "Status"].map((h) => (
                <th key={h} className="px-3 py-2 text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id || r.Id}
                className="border-t border-stone-100 hover:bg-orange-50 cursor-pointer"
                onClick={() => setEditing(r)}
              >
                <td className="px-3 py-2">{r.invoiceNo || r.InvoiceNo}</td>
                <td className="px-3 py-2">{r.parcelId || r.ParcelId || "—"}</td>
                <td className="px-3 py-2">{(r.bookingDate || r.BookingDate || "").toString().slice(0, 10)}</td>
                <td className="px-3 py-2">{r.route || r.Route}</td>
                <td className="px-3 py-2">{r.senderName || r.SenderName}</td>
                <td className="px-3 py-2">{r.priceCharged || r.PriceCharged}</td>
                <td className="px-3 py-2">{r.deliveryLastStatus || r.DeliveryLastStatus}</td>
                <td className="px-3 py-2">{String(r.bdDeliveryDate || r.BdDeliveryDate || "").slice(0, 10)}</td>
                <td className="px-3 py-2">{r.paymentStatus || r.PaymentStatus}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing ? (
        <CrmDrawer title={editing.id || editing.Id ? "Order" : "New order"} onClose={() => setEditing(null)}>
          {editing.id || editing.Id ? (
            <AttendBar
              relatedType="order"
              relatedId={editing.id || editing.Id}
              phone={editing.senderPhone || editing.SenderPhone}
            />
          ) : null}
          <form className="grid grid-cols-1 md:grid-cols-2 gap-3" onSubmit={save}>
            <Field label="Invoice no">
              <TextInput value={editing.invoiceNo ?? editing.InvoiceNo} onChange={(v) => setEditing({ ...editing, invoiceNo: v })} />
            </Field>
            <Field label="Parcel / booking id">
              <TextInput
                type="number"
                value={editing.parcelId ?? editing.ParcelId ?? ""}
                onChange={(v) => setEditing({ ...editing, parcelId: v === "" ? null : Number(v) })}
              />
            </Field>
            <Field label="Month">
              <TextInput value={editing.monthName ?? editing.MonthName} onChange={(v) => setEditing({ ...editing, monthName: v })} />
            </Field>
            <Field label="Boxes">
              <TextInput type="number" value={editing.boxQty ?? editing.BoxQty} onChange={(v) => setEditing({ ...editing, boxQty: v })} />
            </Field>
            <Field label="Route">
              <SelectInput value={editing.route ?? editing.Route} onChange={(v) => setEditing({ ...editing, route: v })} options={ROUTES} />
            </Field>
            <Field label="Handled by">
              <select
                className="w-full border border-stone-300 h-10 rounded-xl px-2"
                value={editing.handledByUserId ?? editing.HandledByUserId ?? ""}
                onChange={(e) => {
                  const id = e.target.value;
                  const u = staff.find((s) => (s.id || s.Id) === id);
                  setEditing({
                    ...editing,
                    handledByUserId: id,
                    handledByName: u?.userName || u?.UserName || "",
                  });
                }}
              >
                <option value="">Select</option>
                {staff.map((u) => (
                  <option key={u.id || u.Id} value={u.id || u.Id}>
                    {u.userName || u.UserName}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Source">
              <TextInput value={editing.source ?? editing.Source} onChange={(v) => setEditing({ ...editing, source: v })} />
            </Field>
            <Field label="Sender">
              <TextInput value={editing.senderName ?? editing.SenderName} onChange={(v) => setEditing({ ...editing, senderName: v })} />
            </Field>
            <Field label="Sender phone">
              <TextInput value={editing.senderPhone ?? editing.SenderPhone} onChange={(v) => setEditing({ ...editing, senderPhone: v })} />
            </Field>
            <Field label="Receiver">
              <TextInput value={editing.receiverName ?? editing.ReceiverName} onChange={(v) => setEditing({ ...editing, receiverName: v })} />
            </Field>
            <Field label="Receiver phone">
              <TextInput value={editing.receiverPhone ?? editing.ReceiverPhone} onChange={(v) => setEditing({ ...editing, receiverPhone: v })} />
            </Field>
            <Field label="Pickup address">
              <TextInput value={editing.pickupAddress ?? editing.PickupAddress} onChange={(v) => setEditing({ ...editing, pickupAddress: v })} />
            </Field>
            <Field label="Delivery address">
              <TextInput value={editing.deliveryAddress ?? editing.DeliveryAddress} onChange={(v) => setEditing({ ...editing, deliveryAddress: v })} />
            </Field>
            <Field label="Item">
              <TextInput value={editing.itemDescription ?? editing.ItemDescription} onChange={(v) => setEditing({ ...editing, itemDescription: v })} />
            </Field>
            <Field label="Price charged">
              <TextInput type="number" value={editing.priceCharged ?? editing.PriceCharged} onChange={(v) => setEditing({ ...editing, priceCharged: v })} />
            </Field>
            <Field label="Payment status">
              <SelectInput value={editing.paymentStatus ?? editing.PaymentStatus} onChange={(v) => setEditing({ ...editing, paymentStatus: v })} options={["Paid", "Pending", "Cancelled"]} />
            </Field>
            <Field label="Payment method">
              <SelectInput value={editing.paymentMethod ?? editing.PaymentMethod} onChange={(v) => setEditing({ ...editing, paymentMethod: v })} options={["Bank Transfer", "Cash", "Stripe"]} />
            </Field>
            <Field label="Tracking no">
              <TextInput value={editing.trackingNumber ?? editing.TrackingNumber} onChange={(v) => setEditing({ ...editing, trackingNumber: v })} />
            </Field>
            <Field label="Tracking link">
              <TextInput value={editing.trackingLink ?? editing.TrackingLink} onChange={(v) => setEditing({ ...editing, trackingLink: v })} />
            </Field>
            <Field label="Delivery date">
              <TextInput
                type="date"
                value={String(editing.bdDeliveryDate || editing.BdDeliveryDate || "").slice(0, 10)}
                onChange={(v) => setEditing({ ...editing, bdDeliveryDate: v })}
              />
            </Field>
            <Field label="Carrier status">
              <SelectInput
                value={editing.deliveryLastStatus ?? editing.DeliveryLastStatus}
                onChange={(v) => setEditing({ ...editing, deliveryLastStatus: v, deliveryStatusUpdatedBy: editing.handledByName })}
                options={CARRIER_STATUSES}
              />
            </Field>
            <Field label="UK last status">
              <TextInput value={editing.ukLastStatus ?? editing.UkLastStatus} onChange={(v) => setEditing({ ...editing, ukLastStatus: v })} />
            </Field>
            <Field label="Packing list">
              <TextInput value={editing.packingList ?? editing.PackingList} onChange={(v) => setEditing({ ...editing, packingList: v })} />
            </Field>
            <Field label="Notes">
              <TextInput value={editing.notes ?? editing.Notes} onChange={(v) => setEditing({ ...editing, notes: v })} />
            </Field>
            <div className="md:col-span-2 flex gap-2 mt-2">
              <PrimaryBtn type="submit">Save</PrimaryBtn>
              <GhostBtn onClick={() => setEditing(null)}>Cancel</GhostBtn>
              {editing.id || editing.Id ? (
                <GhostBtn
                  onClick={async () => {
                    await crmSaveTask({
                      title: `Follow up: ${editing.invoiceNo || editing.InvoiceNo || editing.senderName || editing.SenderName || "order"}`,
                      status: "Open",
                      relatedType: "order",
                      relatedId: editing.id || editing.Id,
                      priority: "Medium",
                      assigneeUserIds: editing.handledByUserId || editing.HandledByUserId
                        ? [editing.handledByUserId || editing.HandledByUserId]
                        : [],
                      assigneeUserNames: editing.handledByName || editing.HandledByName
                        ? [editing.handledByName || editing.HandledByName]
                        : [],
                    });
                    Swal.fire({ icon: "success", title: "Task created" });
                  }}
                >
                  Create task
                </GhostBtn>
              ) : null}
              {(editing.id || editing.Id) && (editing.deliveryLastStatus || editing.DeliveryLastStatus) !== "Delivered" ? (
                <PrimaryBtn
                  onClick={async () => {
                    await crmMarkDelivered(editing.id || editing.Id);
                    Swal.fire({ icon: "success", title: "Marked delivered" });
                    setEditing(null);
                    await load();
                  }}
                >
                  Mark delivered
                </PrimaryBtn>
              ) : null}
            </div>
          </form>
          {editing.id || editing.Id ? (
            <AttachmentList relatedType="order" relatedId={editing.id || editing.Id} />
          ) : null}
        </CrmDrawer>
      ) : null}
    </Box>
  );
};

export default CrmOrders;
